'use client';

import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import Link from 'next/link';
import { Wine, Calendar, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const staggerChildren = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero Section with Background Image */}
      <section className="relative overflow-hidden bg-background h-[600px] md:h-[700px]">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/images/generated/hero-restaurant-interior.png)' }}
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>

        {/* Italian Flag Accent Stripe */}
        <div className="absolute bottom-0 left-0 right-0 h-2 flex">
          <div className="flex-1 bg-secondary" />
          <div className="flex-1 bg-foreground" />
          <div className="flex-1 bg-primary" />
        </div>

        {/* Hero Content */}
        <div className="container relative z-10 mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <motion.h1
            className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
            {...fadeInUp}
          >
            Crudo e Vino
          </motion.h1>

          <motion.p
            className="text-lg md:text-2xl text-foreground/90 mb-4 max-w-3xl mx-auto font-serif"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Authenticité Italienne au Cœur de Differdange
          </motion.p>

          <motion.p
            className="text-base md:text-lg text-muted-foreground mb-4 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Cuisine continue • Lundi au samedi, 10h - 22h30 • Parking gratuit
          </motion.p>

          <motion.p
            className="text-base md:text-lg text-muted-foreground mb-12 max-w-2xl mx-auto font-semibold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            Consommation sur place, à emporter et en livraison, appelez-nous !
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              href="/reserver"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-foreground bg-primary rounded-lg hover:opacity-90 transition-all hover:scale-105 shadow-lg"
            >
              Réserver une Table
            </Link>
            <Link
              href="/carte"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-foreground bg-transparent border-2 border-foreground rounded-lg hover:bg-foreground hover:text-background transition-all hover:scale-105"
            >
              Découvrir la Carte
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {/* Wine Card */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col items-center text-center p-8 rounded-lg border border-border/40 bg-background hover:border-primary transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Wine className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-semibold mb-3">
                Carte des Vins Exceptionnelle
              </h3>
              <p className="text-muted-foreground">
                De nombreuses références italiennes soigneusement sélectionnées
              </p>
            </motion.div>

            {/* Booking Card */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col items-center text-center p-8 rounded-lg border border-border/40 bg-background hover:border-secondary transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="font-serif text-xl font-semibold mb-3">
                Réservation en Temps Réel
              </h3>
              <p className="text-muted-foreground">
                Vérifiez la disponibilité et réservez votre table instantanément
              </p>
            </motion.div>

            {/* Location Card */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col items-center text-center p-8 rounded-lg border border-border/40 bg-background hover:border-foreground/50 transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-foreground/10 flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-foreground" />
              </div>
              <h3 className="font-serif text-xl font-semibold mb-3">
                Emplacement Central
              </h3>
              <p className="text-muted-foreground">
                Situé à Auchan Opkorn, parking gratuit et accès facile
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Menu Preview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-4">
              Notre Sélection du Moment
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Menu Item 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex justify-between items-start gap-4 pb-6 border-b border-border/40"
            >
              <div className="flex-1">
                <h3 className="dish-name text-2xl mb-2">CRUDO DI PARMA E BUFALA</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Jambon de Parme, mozzarella di bufala
                </p>
              </div>
              <div className="text-2xl font-semibold text-primary whitespace-nowrap">
                €18,90
              </div>
            </motion.div>

            {/* Menu Item 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex justify-between items-start gap-4 pb-6 border-b border-border/40"
            >
              <div className="flex-1">
                <h3 className="dish-name text-2xl mb-2">FOCACCIA À LA TRUFFE</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Mortadelle IGP, burrata, tomates séchées
                </p>
              </div>
              <div className="text-2xl font-semibold text-primary whitespace-nowrap">
                €18,50
              </div>
            </motion.div>

            {/* Menu Item 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex justify-between items-start gap-4 pb-6"
            >
              <div className="flex-1">
                <h3 className="dish-name text-2xl mb-2">CASARECCE CALABRESE</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Nduja, burrata, sauce tomate, basilic
                </p>
              </div>
              <div className="text-2xl font-semibold text-primary whitespace-nowrap">
                €19,90
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link
              href="/carte"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-foreground bg-transparent border-2 border-border hover:border-foreground rounded-lg transition-all hover:scale-105"
            >
              Voir la Carte Complète
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}